/**
 * The card collection screen, and the trade offers.
 *
 * Kate asked for the cards to be collectable, sellable and upgradeable; she never
 * described a screen, so this is the plainest thing that shows a collection: a grid of
 * cards with the animal, its real-world status, its level, and what it's worth.
 *
 * The animal portrait on each card is drawn from the same silhouette logic as the models,
 * so a bear card looks like the bear you play.
 */
import {
  CARD_KINDS, cardValue, kindOf, MAX_LEVEL, rarityInfo,
  type CardCollection, type CardKind, type OwnedCard,
} from '../cards/cards'
import { SPECIES } from '../animals/species'

export class CardScreen {
  private readonly root: HTMLDivElement
  private readonly collection: CardCollection
  /** Called when an offer is accepted, so the coupons can be paid in. */
  onAccept: ((coupons: number) => void) | null = null
  onOpenChange: ((open: boolean) => void) | null = null

  constructor(collection: CardCollection) {
    this.collection = collection
    this.root = document.createElement('div')
    this.root.className = 'cards'
    this.root.hidden = true
    document.body.append(this.root)
  }

  get isOpen(): boolean {
    return !this.root.hidden
  }

  open(): void {
    this.root.hidden = false
    this.render()
    this.onOpenChange?.(true)
  }

  close(): void {
    this.root.hidden = true
    this.onOpenChange?.(false)
  }

  toggle(): void {
    if (this.isOpen) this.close()
    else this.open()
  }

  private render(): void {
    this.root.innerHTML = ''
    const panel = document.createElement('div')
    panel.className = 'cards-panel'

    const header = document.createElement('header')
    const title = document.createElement('h2')
    title.textContent = 'Squishy cards'
    const count = document.createElement('p')
    count.className = 'cards-count'
    count.textContent =
      `${this.collection.distinctCount} of ${CARD_KINDS.length} · ${this.collection.totalCount} collected`
    header.append(title, count)
    panel.append(header)

    const note = document.createElement('p')
    note.className = 'cards-note'
    note.textContent = 'Cards only come from winning a competition, and which one you get is random.'
    panel.append(note)

    // Trade offers first — they need answering.
    if (this.collection.incomingOffers.length) {
      const offers = document.createElement('section')
      const heading = document.createElement('h3')
      heading.textContent = 'Offers'
      offers.append(heading)
      for (const offer of this.collection.incomingOffers) {
        const kind = kindOf(offer.species)!
        const row = document.createElement('div')
        row.className = 'cards-offer'
        const text = document.createElement('span')
        text.textContent = `${offer.fromName} offers ${offer.askingPrice} coupons for your ${kind.realName} (lvl ${offer.level})`
        const accept = document.createElement('button')
        accept.textContent = 'Accept'
        accept.addEventListener('click', () => {
          const done = this.collection.acceptOffer(offer.id)
          if (done) this.onAccept?.(done.coupons)
          this.render()
        })
        const decline = document.createElement('button')
        decline.className = 'is-decline'
        decline.textContent = 'No'
        decline.addEventListener('click', () => {
          this.collection.declineOffer(offer.id)
          this.render()
        })
        row.append(text, accept, decline)
        offers.append(row)
      }
      panel.append(offers)
    }

    const grid = document.createElement('div')
    grid.className = 'cards-grid'
    // Every card is shown, owned or not, so the collection reads as a set to complete.
    for (const kind of CARD_KINDS) {
      grid.append(this.cardTile(kind, this.collection.get(kind.species)))
    }
    panel.append(grid)

    const done = document.createElement('button')
    done.className = 'cards-done'
    done.textContent = 'Done'
    done.addEventListener('click', () => this.close())
    panel.append(done)

    this.root.append(panel)
  }

  private cardTile(kind: CardKind, owned: OwnedCard | undefined): HTMLElement {
    const rarity = rarityInfo(kind.rarity)
    const tile = document.createElement('div')
    tile.className = `card${owned ? '' : ' is-locked'}`
    tile.style.setProperty('--rarity', rarity.color)

    const art = document.createElement('div')
    art.className = 'card-art'
    art.append(portrait(kind, owned !== undefined))
    tile.append(art)

    const name = document.createElement('p')
    name.className = 'card-name'
    name.textContent = owned ? kind.realName : '???'
    tile.append(name)

    const status = document.createElement('p')
    status.className = 'card-status'
    status.textContent = rarity.label
    tile.append(status)

    if (owned) {
      const pips = document.createElement('div')
      pips.className = 'card-pips'
      for (let i = 0; i < MAX_LEVEL; i++) {
        const pip = document.createElement('span')
        pip.className = i < owned.level ? 'is-on' : ''
        pips.append(pip)
      }
      tile.append(pips)

      const worth = document.createElement('p')
      worth.className = 'card-worth'
      worth.textContent = `${cardValue(kind.species, owned.level)} coupons`
      tile.append(worth)

      const fact = document.createElement('p')
      fact.className = 'card-fact'
      fact.textContent = kind.fact
      tile.append(fact)
    }
    return tile
  }
}

/**
 * A small painted portrait of the animal, drawn on a canvas. Silhouettes match the
 * in-game bodies — four legs and a head for the quadrupeds, wings for the bird, a fluke
 * for the swimmers — so the card is recognisably the animal you play.
 */
function portrait(kind: CardKind, revealed: boolean): HTMLCanvasElement {
  const size = 120
  const canvas = document.createElement('canvas')
  const dpr = Math.min(window.devicePixelRatio, 2)
  canvas.width = size * dpr
  canvas.height = size * dpr
  canvas.style.width = `${size}px`
  canvas.style.height = `${size}px`
  const ctx = canvas.getContext('2d')
  if (!ctx) return canvas
  ctx.scale(dpr, dpr)

  const species = SPECIES[kind.species]
  const body = `#${species.color.toString(16).padStart(6, '0')}`

  // Sky-ish backdrop so the silhouette reads.
  const sky = ctx.createLinearGradient(0, 0, 0, size)
  sky.addColorStop(0, '#25361c')
  sky.addColorStop(1, '#141f10')
  ctx.fillStyle = sky
  ctx.fillRect(0, 0, size, size)

  if (!revealed) {
    ctx.fillStyle = '#3d4a35'
    ctx.font = 'bold 46px system-ui, sans-serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText('?', size / 2, size / 2)
    return canvas
  }

  ctx.fillStyle = body
  ctx.strokeStyle = 'rgba(0,0,0,0.35)'
  ctx.lineWidth = 2

  const swimmer = kind.species === 'dolphin' || kind.species === 'whale' || kind.species === 'fish'
  if (swimmer) {
    ctx.beginPath()
    ctx.ellipse(size * 0.52, size * 0.55, size * 0.3, size * 0.15, -0.12, 0, Math.PI * 2)
    ctx.fill()
    ctx.beginPath()                                  // fluke
    ctx.moveTo(size * 0.8, size * 0.55)
    ctx.lineTo(size * 0.95, size * 0.4)
    ctx.lineTo(size * 0.95, size * 0.7)
    ctx.closePath()
    ctx.fill()
    ctx.beginPath()                                  // dorsal
    ctx.moveTo(size * 0.5, size * 0.42)
    ctx.lineTo(size * 0.58, size * 0.24)
    ctx.lineTo(size * 0.62, size * 0.43)
    ctx.closePath()
    ctx.fill()
  } else if (kind.species === 'bird') {
    ctx.beginPath()
    ctx.ellipse(size * 0.52, size * 0.58, size * 0.17, size * 0.21, 0.2, 0, Math.PI * 2)
    ctx.fill()
    ctx.beginPath()
    ctx.arc(size * 0.4, size * 0.36, size * 0.11, 0, Math.PI * 2)
    ctx.fill()
    ctx.beginPath()                                  // beak
    ctx.moveTo(size * 0.3, size * 0.36)
    ctx.lineTo(size * 0.16, size * 0.4)
    ctx.lineTo(size * 0.3, size * 0.43)
    ctx.closePath()
    ctx.fillStyle = '#e0a83c'
    ctx.fill()
    ctx.fillStyle = body
    ctx.beginPath()                                  // wing
    ctx.ellipse(size * 0.58, size * 0.54, size * 0.16, size * 0.08, -0.5, 0, Math.PI * 2)
    ctx.fill()
  } else {
    // Quadruped: barrel body, four legs, neck, head, ear, tail.
    const legY = size * 0.72
    ctx.beginPath()
    ctx.ellipse(size * 0.52, size * 0.55, size * 0.24, size * 0.14, 0, 0, Math.PI * 2)
    ctx.fill()
    ctx.lineWidth = size * 0.055
    ctx.strokeStyle = body
    for (const x of [0.36, 0.46, 0.6, 0.7]) {
      ctx.beginPath()
      ctx.moveTo(size * x, size * 0.6)
      ctx.lineTo(size * x, legY + size * 0.12)
      ctx.stroke()
    }
    ctx.beginPath()                                  // neck
    ctx.lineWidth = size * 0.1
    ctx.moveTo(size * 0.33, size * 0.52)
    ctx.lineTo(size * 0.24, size * 0.36)
    ctx.stroke()
    ctx.beginPath()                                  // head
    ctx.ellipse(size * 0.22, size * 0.32, size * 0.1, size * 0.085, -0.3, 0, Math.PI * 2)
    ctx.fill()
    ctx.beginPath()                                  // snout
    ctx.ellipse(size * 0.12, size * 0.35, size * 0.055, size * 0.04, -0.2, 0, Math.PI * 2)
    ctx.fill()
    // Ears vary, the way the models do.
    if (kind.species === 'bunny') {
      for (const dx of [-0.02, 0.03]) {
        ctx.beginPath()
        ctx.ellipse(size * (0.24 + dx), size * 0.18, size * 0.028, size * 0.1, dx * 4, 0, Math.PI * 2)
        ctx.fill()
      }
    } else if (kind.species === 'bear') {
      for (const dx of [-0.03, 0.05]) {
        ctx.beginPath()
        ctx.arc(size * (0.23 + dx), size * 0.23, size * 0.035, 0, Math.PI * 2)
        ctx.fill()
      }
    } else {
      for (const dx of [-0.02, 0.05]) {
        ctx.beginPath()
        ctx.moveTo(size * (0.21 + dx), size * 0.25)
        ctx.lineTo(size * (0.25 + dx), size * 0.14)
        ctx.lineTo(size * (0.28 + dx), size * 0.26)
        ctx.closePath()
        ctx.fill()
      }
    }
    if (kind.species === 'deer') {                   // antlers
      ctx.strokeStyle = '#8a7a5c'
      ctx.lineWidth = size * 0.022
      for (const dir of [-1, 1]) {
        ctx.beginPath()
        ctx.moveTo(size * 0.24, size * 0.24)
        ctx.lineTo(size * (0.24 + dir * 0.07), size * 0.08)
        ctx.stroke()
      }
    }
    if (kind.species === 'camel') {                  // hump
      ctx.fillStyle = body
      ctx.beginPath()
      ctx.arc(size * 0.55, size * 0.42, size * 0.1, Math.PI, 0)
      ctx.fill()
    }
    ctx.strokeStyle = body                           // tail
    ctx.lineWidth = size * 0.035
    ctx.beginPath()
    ctx.moveTo(size * 0.75, size * 0.5)
    ctx.lineTo(size * 0.86, size * 0.4)
    ctx.stroke()
  }
  return canvas
}
